import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, ExternalLink, FileText, Loader2, Send, Upload, Workflow } from "lucide-react";

const LABELS = { app: "Loan application", credit: "Credit report", aus: "AUS report" };

const openFile = async (url) => {
  try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch (e) { toast.error(apiError(e)); }
};

const Check = ({ ok, label, testId }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testId}>
    {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
    <span className={ok ? "text-slate-700" : "text-slate-500"}>{label}</span>
  </div>
);

const ProcUpload = ({ f, kind, onChange }) => {
  const [busy, setBusy] = useState(false);
  const file = f.files[kind];
  const up = async (x) => {
    if (!x) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", x);
    try { onChange((await api.post(`/processing/${f.id}/upload/${kind}`, fd)).data); toast.success(`${LABELS[kind]} uploaded`); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3" data-testid={`proc-${kind}-${f.id}`}>
      <Check ok={!!file} label={LABELS[kind]} />
      <div className="flex items-center gap-2">
        {file && <Button size="sm" variant="ghost" onClick={() => openFile(`/processing/${f.id}/view/${kind}`)} data-testid={`proc-${kind}-view-${f.id}`}><FileText className="mr-1 h-4 w-4" />{file.filename}</Button>}
        <label className="cursor-pointer">
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { up(e.target.files[0]); e.target.value = ""; }} data-testid={`proc-${kind}-input-${f.id}`} />
          <span className="inline-flex h-8 items-center rounded-md border border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1 h-3 w-3" />{file ? "Replace" : "Upload"}</>}</span>
        </label>
      </div>
    </div>
  );
};

const FileCard = ({ f, onChange, processor }) => {
  const resend = async () => {
    try { onChange((await api.post(`/processing/${f.id}/resend`)).data); toast.success(`Sent to ${processor}`); } catch (e) { toast.error(apiError(e)); }
  };
  const incomeIn = f.income.filter((d) => d.received).length;
  return (
    <Card className="p-5" data-testid={`proc-file-${f.id}`}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-display text-lg font-bold text-slate-900">{f.borrower_name}</div>
          <div className="text-xs text-slate-500">Realtor: {f.realtor_name} · {new Date(f.created_at).toLocaleDateString()}</div>
        </div>
        {f.sent?.at ? <Badge className="border-0 bg-emerald-50 text-emerald-800" data-testid={`proc-sent-${f.id}`}>Sent to {processor} {new Date(f.sent.at).toLocaleString()}</Badge>
          : f.sent?.error ? <Badge className="border-0 bg-rose-50 text-rose-700" data-testid={`proc-sent-${f.id}`}>Send failed - {f.sent.error}</Badge>
          : f.needs_mlo_account ? <Badge className="border-0 bg-amber-50 text-amber-800" data-testid={`proc-sent-${f.id}`}>Can't send until the loan officer has an account - reassign it in Referrals</Badge>
          : <Badge className="border-0 bg-slate-100 text-slate-700" data-testid={`proc-sent-${f.id}`}>Not sent to {processor} yet</Badge>}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg bg-slate-50 p-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600" data-testid={`proc-income-count-${f.id}`}>Income documents ({incomeIn}/{f.income.length} received)</div>
          <div className="space-y-1">{f.income.map((d) => <Check key={d.key} ok={d.received} label={d.label} testId={`proc-income-${d.key}-${f.id}`} />)}</div>
          <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
            <Check ok={f.pre_approval} label="Pre-approval letter" />
            <Check ok={f.loan_estimate} label="Loan estimate (optional)" />
          </div>
        </div>
        <div className="space-y-2">{Object.keys(LABELS).map((k) => <ProcUpload key={k} f={f} kind={k} onChange={onChange} />)}
          {f.sent?.at && <Button size="sm" variant="outline" onClick={resend} data-testid={`proc-resend-${f.id}`}><Send className="mr-1 h-4 w-4" />Send again</Button>}
        </div>
      </div>
    </Card>
  );
};

const AdminSettings = ({ data, onSaved }) => {
  const [s, setS] = useState({ arive_url: data.arive_url, processor_email: data.processor_email });
  const save = async () => {
    try { await api.put("/processing-settings", s); toast.success("Saved"); onSaved(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="flex flex-wrap items-end gap-3 p-4" data-testid="proc-settings">
      <div className="min-w-[240px] flex-1"><Label className="text-xs">Arive link</Label><Input value={s.arive_url} onChange={(e) => setS({ ...s, arive_url: e.target.value })} data-testid="proc-arive-url" /></div>
      <div className="min-w-[240px] flex-1"><Label className="text-xs">Processor email</Label><Input value={s.processor_email} onChange={(e) => setS({ ...s, processor_email: e.target.value })} data-testid="proc-processor-email" /></div>
      <Button onClick={save} data-testid="proc-settings-save">Save</Button>
    </Card>
  );
};

export default function LoanProcessing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/processing").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const update = (row) => setData((d) => ({ ...d, files: d.files.map((x) => (x.id === row.id ? row : x)) }));
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="processing-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold text-slate-900"><Workflow className="h-7 w-7 text-amber-500" />Loan Processing &amp; LOS</h1>
          <p className="max-w-2xl text-sm text-slate-600">Upload the application, credit report and AUS for each borrower. Once the borrower has sent every income document,
            the full file (income documents, application, credit, AUS, loan estimate and pre-approval) is emailed to {data.processor_name} ({data.processor_email}) and Ava calls you.</p>
        </div>
        <a href={data.arive_url} target="_blank" rel="noreferrer" data-testid="arive-btn"
          className="gold-cta inline-flex h-10 items-center rounded-md px-4 text-sm font-bold">Open Arive<ExternalLink className="ml-2 h-4 w-4" /></a>
      </div>
      {user?.role === "admin" && <AdminSettings data={data} onSaved={load} />}
      <div className="space-y-4">
        {data.files.length ? data.files.map((f) => <FileCard key={f.id} f={f} onChange={update} processor={data.processor_name} />)
          : <p className="py-8 text-center text-sm text-slate-500" data-testid="processing-empty">No borrowers yet - send a referral first and it will show up here.</p>}
      </div>
    </div>
  );
}
